// admin/routes/RouteDetailDrawer.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { X, Edit, Eye, EyeOff, Trash2, Save, XCircle } from "lucide-react";
import RouteDetailMap from "../../map/RouteDetailMap";
import Button from "../../common/Button";
import { editRoute, hideRoute, unhideRoute, deleteRoute } from "@/store/slices/adminSlice";
import EntityAuditHistory from "../audit/EntityAuditHistory";

const RouteDetailDrawer = ({ route, onClose }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("details");
  const { isUpdating, updateError } = useSelector((state) => state.admin);
  
  const [isAnimating, setIsAnimating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showHideDialog, setShowHideDialog] = useState(false);
  const [hideReason, setHideReason] = useState("");
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    name: route.name || "",
    description: route.description || "",
    difficulty: route.difficulty || "",
    distance: route.distance || "",
    start_point: route.start_point || "",
    end_point: route.end_point || "",
    state_code: route.state_code || route.state || "",
  });

  // Trigger animation on mount
  useEffect(() => {
    setIsAnimating(true);
  }, []);

  // Handle close with animation
  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 300);
  };

  // Handle edit save
  const handleSaveEdit = async () => {
    try {
      await dispatch(editRoute({ 
        routeId: route._id, 
        updates: editForm 
      })).unwrap();
      setIsEditing(false);
    } catch (error) {
      console.error("Edit failed:", error);
    }
  };

  // Handle edit cancel
  const handleCancelEdit = () => {
    setEditForm({
      name: route.name || "",
      description: route.description || "",
      difficulty: route.difficulty || "",
      distance: route.distance || "",
      start_point: route.start_point || "",
      end_point: route.end_point || "",
      state_code: route.state_code || route.state || "",
    });
    setIsEditing(false);
  };

  // Handle hide
  const handleHide = async () => {
    try {
      await dispatch(hideRoute({ 
        routeId: route._id, 
        rejection_reason: hideReason.trim() || undefined 
      })).unwrap();
      setShowHideDialog(false);
      setHideReason("");
      handleClose();
    } catch (error) {
      console.error("Hide failed:", error);
    }
  };

  // Handle unhide
  const handleUnhide = async () => {
    try {
      await dispatch(unhideRoute(route._id)).unwrap();
      handleClose();
    } catch (error) {
      console.error("Unhide failed:", error);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      await dispatch(deleteRoute(route._id)).unwrap();
      handleClose();
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  // Determine which visibility button to show
  const showHideButton = route.status === "approved";
  const showUnhideButton = route.status === "hidden";

  return (
    <div className="fixed inset-0 z-[100] flex">
      {/* Backdrop */}
      <div
        className={`flex-1 bg-black/40 transition-opacity duration-300 ${
          isAnimating ? "opacity-100" : "opacity-0"
        }`}
        onClick={handleClose}
      />

      {/* Drawer - slides in from right */}
      <aside
        className={`w-full sm:w-96 md:w-[32rem] lg:w-[40rem] max-w-full bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-y-auto shadow-2xl transform transition-transform duration-300 ease-out ${
          isAnimating ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-10">
          <div className="px-4 sm:px-6 pt-4 pb-2">
            <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-gray-100 w-full bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-green-500"
                />
              ) : (
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-gray-100 truncate">
                  {route.name}
                </h3>
              )}
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                by {route.creator_id?.name || route.creator?.name || "Unknown"}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="ml-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          </div>
          <div className="flex px-4 sm:px-6">
            <button
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                activeTab === "details"
                  ? "text-green-600 border-green-600"
                  : "text-gray-500 border-transparent hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              }`}
              onClick={() => setActiveTab("details")}
            >
              Details
            </button>
            <button
               className={`px-4 py-2 text-sm font-medium border-b-2 ${
                 activeTab === "history"
                   ? "text-green-600 border-green-600"
                   : "text-gray-500 border-transparent hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
               }`}
               onClick={() => setActiveTab("history")}
            >
              Audit History
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-6">
          {activeTab === "details" ? (
            <>
          {/* Map Preview */}
          <div>
            <h4 className="font-medium text-gray-800 dark:text-gray-100 mb-2">
              Map Preview
            </h4>
            <div className="h-48 sm:h-60 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              <RouteDetailMap 
                route={{
                  ...route,
                  coordinates: route.coordinates?.coordinates || route.coordinates || []
                }} 
              />
            </div>
          </div>

          {/* Details */}
          <div>
            <h4 className="font-medium text-gray-800 dark:text-gray-100 mb-3">
              Details
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700 dark:text-gray-300">
              <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
                <div className="text-xs text-gray-500 dark:text-gray-400">Distance</div>
                {isEditing ? (
                  <input
                    type="number"
                    step="0.1"
                    value={editForm.distance}
                    onChange={(e) => setEditForm({ ...editForm, distance: e.target.value })}
                    className="font-semibold mt-1 w-full bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-green-500"
                  />
                ) : (
                  <div className="font-semibold mt-1">{route.distance} km</div>
                )}
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
                <div className="text-xs text-gray-500 dark:text-gray-400">Difficulty</div>
                {isEditing ? (
                  <select
                    value={editForm.difficulty}
                    onChange={(e) => setEditForm({ ...editForm, difficulty: e.target.value })}
                    className="font-semibold mt-1 w-full bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-green-500"
                  >
                    <option value="easy">Easy</option>
                    <option value="moderate">Moderate</option>
                    <option value="hard">Hard</option>
                  </select>
                ) : (
                  <div className="font-semibold mt-1 capitalize">{route.difficulty || "N/A"}</div>
                )}
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
                <div className="text-xs text-gray-500 dark:text-gray-400">State</div>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.state_code}
                    onChange={(e) => setEditForm({ ...editForm, state_code: e.target.value.toUpperCase() })}
                    maxLength={2}
                    placeholder="e.g., CA"
                    className="font-semibold mt-1 w-full bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-green-500"
                  />
                ) : (
                  <div className="font-semibold mt-1">{route.state_code || route.state || "N/A"}</div>
                )}
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
                <div className="text-xs text-gray-500 dark:text-gray-400">Submitted</div>
                <div className="font-semibold mt-1">
                  {new Date(route.created_at).toLocaleDateString()}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
                <div className="text-xs text-gray-500 dark:text-gray-400">Status</div>
                <div className="font-semibold mt-1 capitalize">{route.status}</div>
              </div>
            </div>

            {/* Hide Reason Display - Only shown when route is hidden */}
            {route.status === "hidden" && route.rejection_reason && (
              <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <EyeOff className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h5 className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-1">
                      Hide Reason
                    </h5>
                    <p className="text-sm text-yellow-700 dark:text-yellow-400">
                      {route.rejection_reason}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Additional editable fields */}
            {isEditing && (
              <div className="mt-4 space-y-3">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Description</label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows={3}
                    className="w-full mt-1 p-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-green-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Start Point</label>
                    <input
                      type="text"
                      value={editForm.start_point}
                      onChange={(e) => setEditForm({ ...editForm, start_point: e.target.value })}
                      className="w-full mt-1 p-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">End Point</label>
                    <input
                      type="text"
                      value={editForm.end_point}
                      onChange={(e) => setEditForm({ ...editForm, end_point: e.target.value })}
                      className="w-full mt-1 p-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-green-500"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Error Display */}
          {updateError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{updateError}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            {isEditing ? (
              <>
                <Button
                  variant="primary"
                  onClick={handleSaveEdit}
                  disabled={isUpdating}
                  className="flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {isUpdating ? "Saving..." : "Save Changes"}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleCancelEdit}
                  disabled={isUpdating}
                  className="flex items-center justify-center gap-2"
                >
                  <XCircle className="w-4 h-4" />
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  disabled={isUpdating}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 font-medium transition-colors disabled:opacity-50"
                >
                  <Edit className="w-4 h-4" />
                  Edit Route
                </button>

                {showHideButton && (
                  <button
                    onClick={() => setShowHideDialog(true)}
                    disabled={isUpdating}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-900/50 font-medium transition-colors disabled:opacity-50"
                  >
                    <EyeOff className="w-4 h-4" />
                    Hide Route
                  </button>
                )}

                {showUnhideButton && (
                  <button
                    onClick={handleUnhide}
                    disabled={isUpdating}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 font-medium transition-colors disabled:opacity-50"
                  >
                    <Eye className="w-4 h-4" />
                    {isUpdating ? "Unhiding..." : "Unhide Route"}
                  </button>
                )}

                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isUpdating}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 font-medium transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Route
                </button>
              </>
            )}
          </div>
            </>
          ) : (
            <EntityAuditHistory entityId={route._id} entityType="Route" />
          )}
        </div>
      </aside>

      {/* Hide Dialog */}
      {showHideDialog && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowHideDialog(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Hide Route
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Optionally provide a reason for hiding this route:
            </p>
            <textarea
              value={hideReason}
              onChange={(e) => setHideReason(e.target.value)}
              placeholder="Reason for hiding (optional)..."
              rows={3}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <div className="flex gap-3 mt-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowHideDialog(false);
                  setHideReason("");
                }}
                disabled={isUpdating}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleHide}
                disabled={isUpdating}
                className="flex-1 bg-yellow-600 hover:bg-yellow-700"
              >
                {isUpdating ? "Hiding..." : "Hide Route"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Delete Route
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to permanently delete this route? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isUpdating}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={isUpdating}
                className="flex-1"
              >
                {isUpdating ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteDetailDrawer;
